import { Checkbox, Field, Text, type PlaybackTransport } from "@carbon6/c6-frontend-design";
import { Fragment, useId, type ReactNode } from "react";
import { hatcheryTextModes } from "@/app/theme";
import { HatTextTooltip } from "@/components/tooltip";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { DIAGNOSTICS_GROUP_TERMS } from "../diagnosticsTerms";
import type { ParquetChannelGroupId } from "../parquetChannels";
import {
  hasBothArms,
  motionCardRows,
  MOTION_SERIES_STYLE,
  type MotionCardSeries,
  type MotionChannelCard,
} from "./motionChannelCards";
import type { MotionWindow } from "./motionSeries";
import { motionSeriesKey, type MotionSeriesSource } from "./motionRendering";
import { motionMetricTerm } from "./motionTerms";
import { MotionPlot, type MotionPlotLine } from "./MotionPlot";
import band from "../EpisodeParquetDeviationVisual.module.css";
import styles from "./MotionChannelGrid.module.css";

/** 꺼진 계열만 담는다. 기본값이 "켬"이므로 새 카드는 초기화 없이 켜진 상태로 표시된다. */
export type HiddenSeries = ReadonlySet<string>;

export function hiddenSeriesKey(cardId: string, group: ParquetChannelGroupId): string {
  return `${cardId}\u0000${group}`;
}

const ARM_LABEL = { left: "Left", right: "Right" } as const;

type Props = {
  cards: readonly MotionChannelCard[];
  /** (채널, 지표) → 원본 계열. 없으면 그 계열은 아직 오지 않았다. */
  source: MotionSeriesSource;
  window: MotionWindow;
  transport: PlaybackTransport | null;
  onSelect: (seconds: number) => void;
  hidden: HiddenSeries;
  onToggle: (cardId: string, group: ParquetChannelGroupId, shown: boolean) => void;
  /** 카드가 하나도 없을 때의 문구. */
  empty: string;
};

/**
 * 채널 카드 격자. 카드 하나가 채널 하나다. 카드 안에서 지표를 세로로 쌓는다.
 *
 * 양팔 데이터셋에서는 **좌우를 한 행에 나란히** 둔다(열=팔). 세로로 떨어뜨리면 같은 채널의 두
 * 팔을 한 번에 비교할 수 없다. 짝이 없는 쪽은 빈 칸으로 남긴다. 접으면 아래 행이 한 칸씩
 * 이동해 좌우 열이 어긋난다. 한 팔뿐이거나 팔이 없는 데이터셋에서는 빈 열을 만들지 않고
 * 카드를 순서대로 배치한다.
 *
 * 카드 외곽은 채널 밴드의 스타일을 그대로 쓴다(`band.*`). 격자 트랙만 따로 정한다. 밴드 카드의
 * 최소 폭은 시간축 차트에 너무 좁다.
 */
export function MotionChannelGrid({ cards, empty, ...rest }: Props): ReactNode {
  if (cards.length === 0) return <EmptyState size="sm">{empty}</EmptyState>;
  const rows = motionCardRows(cards);
  // 좌우 열은 양팔일 때만 만든다. 한 팔뿐이면 빈 열이 폭의 절반을 차지한다.
  const paired = hasBothArms(cards);
  const arms = paired ? rows.flatMap((row) => (row.kind === "arms" ? [row] : [])) : [];
  const rest_ = paired ? rows.flatMap((row) => (row.kind === "common" ? [row.card] : [])) : cards;
  return (
    <>
      <Legend />
      {arms.length > 0 ? (
        <div className={styles.arms}>
          {(["left", "right"] as const).map((side) => (
            <div className={styles.armHead} key={side}>
              <Text variant={hatcheryTextModes["caption-bold"]}>{ARM_LABEL[side]}</Text>
              <Text variant={hatcheryTextModes["caption-normal"]} className={styles.armCount}>
                {arms.filter((row) => row[side] !== null).length}개 채널
              </Text>
            </div>
          ))}
          {arms.map((row) => (
            <Fragment key={row.key}>
              {(["left", "right"] as const).map((side) =>
                row[side] === null ? (
                  <EmptyState key={side}>짝이 되는 {ARM_LABEL[side]} 채널이 없습니다.</EmptyState>
                ) : (
                  <Card card={row[side]} key={side} {...rest} />
                ),
              )}
            </Fragment>
          ))}
        </div>
      ) : null}
      {rest_.length > 0 ? (
        <div className={styles.grid}>
          {arms.length > 0 ? (
            <div className={band.groupHead}>
              <span className={band.groupTitle}>공통</span>
              <span className={band.groupCount}>{rest_.length}개 채널</span>
            </div>
          ) : null}
          {rest_.map((card) => (
            <Card card={card} key={card.id} {...rest} />
          ))}
        </div>
      ) : null}
    </>
  );
}

/** 계열 표기는 모든 카드가 같다. 그래서 범례는 격자 위 한 줄만 둔다. */
function Legend(): ReactNode {
  return (
    <div className={styles.legend}>
      {(["observation", "action"] as const).map((group) => (
        <HatTextTooltip key={group} text={DIAGNOSTICS_GROUP_TERMS[group].tip} placement="bottom">
          <span className={styles.legendItem} data-group={group}>
            <span className={styles.legendLine} aria-hidden="true" />
            <Text variant={hatcheryTextModes["caption-normal"]}>
              {DIAGNOSTICS_GROUP_TERMS[group].label}
            </Text>
          </span>
        </HatTextTooltip>
      ))}
    </div>
  );
}

function Card({
  card,
  source,
  window,
  transport,
  onSelect,
  hidden,
  onToggle,
}: Omit<Props, "cards" | "empty"> & { card: MotionChannelCard }): ReactNode {
  const ids = useId();
  const shown = (series: MotionCardSeries) => !hidden.has(hiddenSeriesKey(card.id, series.group));
  const lines = (metric: string): MotionPlotLine[] =>
    card.series.flatMap((series) => {
      const found = shown(series) ? source.get(motionSeriesKey(series.channel, metric)) : undefined;
      return found === undefined
        ? []
        : [
            {
              series: found.series,
              preview: found.preview,
              title: series.title,
              ...MOTION_SERIES_STYLE[series.group],
            },
          ];
    });
  return (
    <section className={band.column} aria-label={card.title}>
      <div className={band.columnHead}>
        <span className={band.columnTitle}>{card.title}</span>
      </div>
      <div className={styles.sources}>
        {card.series.map((series) => {
          const term = DIAGNOSTICS_GROUP_TERMS[series.group];
          return (
            // 체크박스 면·윤곽과 라벨 글자를 그 계열의 선 색에 맞춘다. 그래서 사용자는 이름을
            // 읽기 전에 색으로 체크박스와 선을 연결한다. c6 제어 토큰을 이 줄 안에서만 다시
            // 매핑한다. 그래서 focus·disabled 판정은 c6 가 계속 소유한다.
            <span className={styles.source} data-group={series.group} key={series.group}>
              <Field
                layout="hug"
                labelPlacement="end"
                id={`${ids}${series.group}`}
                // 색 있는 라벨은 노드로 전달한다. Field 는 문자열 라벨의 색을 직접 소유한다.
                label={
                  <Text
                    variant={hatcheryTextModes["caption-normal"]}
                    className={styles.sourceLabel}
                  >
                    {term.label}
                  </Text>
                }
              >
                <Checkbox
                  size="1em"
                  checked={shown(series)}
                  // 같은 이름의 체크박스가 카드 수만큼 생긴다. 그래서 접근명에 채널을 넣는다.
                  aria-label={`${card.title} ${term.label}`}
                  onChange={(event) => onToggle(card.id, series.group, event.target.checked)}
                />
              </Field>
            </span>
          );
        })}
      </div>
      {card.metrics.map((metric) => (
        <MotionPlot
          key={metric}
          label={motionMetricTerm(metric).label}
          titlePrefix={card.title}
          lines={lines(metric)}
          window={window}
          transport={transport}
          onSelect={onSelect}
        />
      ))}
    </section>
  );
}
